import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import axios from 'axios';

interface ImageUploadProps {
  onImageUpload: (imageUrl: string) => void;
  currentImage?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ onImageUpload, currentImage }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImage || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when currentImage changes (e.g., when editing a different post)
  useEffect(() => {
    setPreviewUrl(currentImage || null);
    setIsUploaded(!!currentImage);
    setSelectedFile(null);
    setError(null);
  }, [currentImage]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.match('image.*')) {
      setError('Vælg venligst en billedfil (JPEG, PNG, etc.)');
      return;
    }

    // Check file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError('Filen er for stor. Maksimal størrelse er 20MB.');
      return;
    }

    setSelectedFile(file);
    setError(null);
    setIsUploaded(false);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);

    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('image', selectedFile);
      
      // Add the previous image URL if it exists
      if (currentImage) {
        formData.append('previousImage', currentImage);
      }

      // Send the file to the server
      const response = await axios.post(
	'/api/upload', 
	formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      // Use the URL returned from the server
      if (response.data && response.data.imageUrl) {
        onImageUpload(response.data.imageUrl);
        setIsUploaded(true);
      } else {
        // Fallback to using the preview URL if server doesn't return a URL
        // This is for backward compatibility
        if (previewUrl) {
          onImageUpload(previewUrl);
          setIsUploaded(true);
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Der opstod en fejl under upload af billedet. Prøv igen senere.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsUploaded(false);
    setError(null);
    
    // Clear the file input
    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <div>
          <label htmlFor="image-upload" className="block text-sm font-medium text-gray-700 mb-1">
            Vælg billede
          </label>
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-snudefryd-blue file:text-white
              hover:file:bg-snudefryd-blue/80
              cursor-pointer"
          />
        </div>
        <div className="flex space-x-2 mt-6">
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading || isUploaded}
            className=""
          >
            {isUploading ? 'Uploader...' : isUploaded ? 'Uploadet' : 'Upload'}
          </Button>
          
          {isUploaded && (
            <Button
              type="button"
              onClick={handleReset}
              variant="outline"
              className=""
            >
              Nyt billede
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-red-500 text-sm mt-1">
          {error}
        </div>
      )}

      {previewUrl && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Forhåndsvisning</p>
          <div className="relative w-full h-48 bg-gray-100 rounded-md overflow-hidden">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload; 
